import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  DtiPolicy,
  HemBenchmark,
  LenderPolicy,
  StampDutyBracket,
} from '../types';

interface StateTaxRateResponse {
  id: string;
  stateCode: string;
  minPropertyValue: number | string;
  maxPropertyValue: number | string;
  baseDutyAud: number | string;
  rateOnExcessPer100: number | string;
  foreignBuyerSurchargePct: number | string;
  absenteeLandTaxPct: number | string;
  sourceName: string | null;
}

interface HemBenchmarkResponse {
  id: string;
  maritalStatus: string;
  dependants: number;
  incomeMin: number | string;
  incomeMax: number | string | null;
  minimumMonthlyExpenseAud: number | string;
}

interface LenderPolicyResponse {
  maxLvrAuCitizen: number | string | null;
  maxLvrAuPr: number | string | null;
  maxLvrForeignNational: number | string | null;
  baseSalaryShade: Record<string, number | string> | null;
  bonusSalaryShade: Record<string, number | string> | null;
  auRentalShade: number | string | null;
  foreignRentalShade: number | string | null;
  productRate: number | string | null;
  productName: string | null;
  creditCardFactor: number | string | null;
  assessmentRateBuffer: number | string | null;
  floorRate: number | string | null;
  dti: DtiPolicy;
  dtiIncomeBasis?: 'assessed' | 'gross' | null;
  dscr: LenderPolicy['dscr'] | undefined;
  isCurrent: boolean;
}

function toNum(v: number | string | null | undefined, fallback: number): number {
  if (v == null) return fallback;
  const n = Number(v);
  return Number.isNaN(n) ? fallback : n;
}

function toNumRecord(
  rec: Record<string, number | string> | null | undefined,
  fallback: Record<string, number>,
): Record<string, number> {
  if (!rec) return fallback;
  const out: Record<string, number> = {};
  for (const [k, v] of Object.entries(rec)) {
    out[k] = Number(v);
  }
  return out;
}

function parseDti(raw: DtiPolicy | undefined): DtiPolicy {
  if (raw == null) return null;
  if (typeof raw === 'number' || typeof raw === 'string') return Number(raw);
  return {
    ownerOccupiedNoRental: Number(raw.ownerOccupiedNoRental),
    ownerOccupiedWithRental: Number(raw.ownerOccupiedWithRental),
    investment: Number(raw.investment),
  };
}

function parseDscr(
  raw: LenderPolicy['dscr'] | undefined,
): LenderPolicy['dscr'] {
  if (!raw) return null;
  const result: LenderPolicy['dscr'] = { minimum: Number(raw.minimum) };
  if (raw.aboveLvr) {
    result!.aboveLvr = {
      lvr: Number(raw.aboveLvr.lvr),
      minimum: Number(raw.aboveLvr.minimum),
    };
  }
  return result;
}

interface LenderResponse {
  id: string;
  name: string;
  displayName: string;
  isActive: boolean;
  logoUrl: string | null;
  tier: number | null;
  policies: LenderPolicyResponse[];
}

interface ForexRatesResponse {
  base: string;
  last_updated: string;
  next_update: string;
  rates: Record<string, number>;
}

export interface ForexRates {
  base: string;
  lastUpdated: string;
  nextUpdate: string;
  rates: Record<string, number>;
}

export const blueprintAPI = createApi({
  reducerPath: 'blueprintAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_ADMIN_URL,
  }),
  endpoints: (builder) => ({
    fetchStampDutyBrackets: builder.query<
      Record<string, StampDutyBracket[]>,
      void
    >({
      query: () => '/v1/public/settings/state-tax-rates',
      transformResponse: (data: StateTaxRateResponse[]) => {
        const grouped: Record<string, StampDutyBracket[]> = {};
        for (const row of data) {
          const code = row.stateCode;
          if (!grouped[code]) grouped[code] = [];
          grouped[code].push({
            minPropertyValue: Number(row.minPropertyValue),
            maxPropertyValue: Number(row.maxPropertyValue),
            baseDutyAud: Number(row.baseDutyAud),
            rateOnExcessPer100: Number(row.rateOnExcessPer100),
            foreignBuyerSurchargePct: Number(row.foreignBuyerSurchargePct),
          });
        }
        for (const code of Object.keys(grouped)) {
          grouped[code].sort(
            (a, b) => a.minPropertyValue - b.minPropertyValue,
          );
        }
        return grouped;
      },
    }),

    fetchHemBenchmarks: builder.query<HemBenchmark[], void>({
      query: () => '/v1/public/settings/hem-benchmarks',
      transformResponse: (json: HemBenchmarkResponse[] | { data: HemBenchmarkResponse[] }) => {
        const rows = Array.isArray(json) ? json : json.data;
        return rows.map((r) => ({
          maritalStatus: r.maritalStatus,
          dependants: r.dependants,
          incomeMin: Number(r.incomeMin),
          incomeMax: r.incomeMax === null ? null : Number(r.incomeMax),
          minimumMonthlyExpenseAud: Number(r.minimumMonthlyExpenseAud),
        }));
      },
    }),

    fetchFxRates: builder.query<ForexRates, void>({
      query: () => ({
        url: `${import.meta.env.VITE_BACKEND_URL}/api/utils/forex/rates/`,
      }),
      transformResponse: (data: ForexRatesResponse): ForexRates => ({
        base: data.base,
        lastUpdated: data.last_updated,
        nextUpdate: data.next_update,
        rates: data.rates,
      }),
    }),

    fetchBlueprintLenders: builder.query<LenderPolicy[], void>({
      query: () =>
        '/v1/public/lender-management/lenders?calculationEnabled=true',
      transformResponse: (
        raw: LenderResponse[] | { data: LenderResponse[] },
      ) => {
        const data = Array.isArray(raw) ? raw : raw.data;
        return data
          .filter((lender) => lender.isActive)
          .map((lender) => {
            const policy =
              lender.policies.find((p) => p.isCurrent) ??
              lender.policies[0];
            const maxLvrCitizen = policy?.maxLvrAuCitizen;
            const maxLvrPr = policy?.maxLvrAuPr;
            const maxLvrForeign = policy?.maxLvrForeignNational;
            return {
              name: lender.name,
              displayName: lender.displayName,
              tier:
                lender.tier === 1 || lender.tier === 2 || lender.tier === 3
                  ? lender.tier
                  : 3,
              logoUrl: lender.logoUrl ?? null,
              maxLvrAuCitizen:
                maxLvrCitizen != null ? Number(maxLvrCitizen) : null,
              maxLvrAuPr:
                maxLvrPr != null ? Number(maxLvrPr) : null,
              maxLvrForeignNational:
                maxLvrForeign != null ? Number(maxLvrForeign) : null,
              baseSalaryShade: toNumRecord(policy?.baseSalaryShade, {
                AUD: 1.0,
                _: 0.6,
              }),
              bonusSalaryShade: toNumRecord(policy?.bonusSalaryShade, {
                AUD: 0.25,
                _: 0.25,
              }),
              auRentalShade: toNum(policy?.auRentalShade, 0.8),
              foreignRentalShade: toNum(policy?.foreignRentalShade, 0.8),
              productRate: toNum(policy?.productRate, 0.065),
              productName: policy?.productName ?? 'Standard Variable',
              creditCardFactor: toNum(policy?.creditCardFactor, 0.038),
              assessmentRateBuffer: toNum(
                policy?.assessmentRateBuffer,
                0.03,
              ),
              floorRate: toNum(policy?.floorRate, 0),
              dti: parseDti(policy?.dti),
              dtiIncomeBasis: policy?.dtiIncomeBasis ?? undefined,
              dscr: parseDscr(policy?.dscr),
            };
          });
      },
    }),
  }),
});

export const {
  useFetchStampDutyBracketsQuery,
  useFetchHemBenchmarksQuery,
  useFetchBlueprintLendersQuery,
  useFetchFxRatesQuery,
} = blueprintAPI;
